import React, { useState, useEffect, useCallback } from 'react';
import style from './StockFollowButton.module.css';

function csrfToken() {
  if (typeof document === 'undefined') return '';
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.content : '';
}

function resolveSignedIn(sessionSignedIn) {
  if (typeof sessionSignedIn === 'boolean') {
    return sessionSignedIn;
  }
  if (typeof document === 'undefined') {
    return false;
  }
  return document.body?.dataset?.userSignedIn === 'true';
}

const DEFAULT_ALERT_ICON = '/assets/svg/add-alert.svg';

function BellAlertIcon({ src }) {
  return (
    <span className={style.iconWrap} aria-hidden="true">
      <img src={src} alt="" className={style.icon} width={20} height={20} />
    </span>
  );
}

function showStockFollowUpsell(name) {
  if (typeof window.showFollowUpsellModal === 'function') {
    window.showFollowUpsellModal({ name, kind: 'stock' });
  }
}

function trackStockFollowSignupClick(symbol) {
  if (typeof gtag === 'function') {
    gtag('event', 'stock_follow_signup_click', {
      event_category: 'stock_follow',
      event_label: symbol,
    });
  }
}

const StockFollowButton = ({ symbol, displayName, initialFollowing, sessionSignedIn, iconSrc }) => {
  const signedIn = resolveSignedIn(sessionSignedIn);
  const [following, setFollowing] = useState(() => {
    if (typeof initialFollowing === 'boolean') return initialFollowing;
    return signedIn ? false : null;
  });
  const sym = symbol ? String(symbol).toLowerCase() : '';

  useEffect(() => {
    if (!signedIn || !sym) {
      if (!signedIn) setFollowing(null);
      return undefined;
    }
    let cancelled = false;
    fetch(`/stocks/${encodeURIComponent(sym)}/follow_status`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then((r) => r.json())
      .then((data) => {
        if (!cancelled) setFollowing(!!data.following);
      })
      .catch(() => {
        if (!cancelled) setFollowing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sym, sessionSignedIn, signedIn]);

  const onClick = useCallback(() => {
    if (!signedIn || following === null) return;
    const wantsFollow = !following;
    const method = wantsFollow ? 'POST' : 'DELETE';
    fetch(`/stocks/${encodeURIComponent(sym)}/follow`, {
      method,
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken(),
      },
    }).then((r) => {
      if (!r.ok) return;
      setFollowing(wantsFollow);
      if (wantsFollow) {
        const label = displayName || sym.toUpperCase();
        showStockFollowUpsell(label);
      }
    });
  }, [displayName, following, sym, signedIn]);

  if (!sym) return null;

  const isFollowing = signedIn && following === true;
  const joinHref = `/join?return_to=${encodeURIComponent(`/stocks/${sym}`)}`;
  const alertIcon = iconSrc || DEFAULT_ALERT_ICON;

  return (
    <div className={style.banner} role="group" aria-label="Follow stock for trade alerts">
      <BellAlertIcon src={alertIcon} />
      <p className={style.copy}>Get informed when a big investor buys or sells</p>
      {!signedIn ? (
        <a
          href={joinHref}
          className={`${style.cta} ${style.ctaPrimary}`}
          onClick={() => trackStockFollowSignupClick(sym)}
        >
          + Follow
        </a>
      ) : (
        <button
          type="button"
          className={`${style.cta} ${isFollowing ? style.ctaFollowing : style.ctaPrimary}`}
          onClick={onClick}
          aria-pressed={isFollowing}
        >
          {isFollowing ? 'Following' : '+ Follow'}
        </button>
      )}
    </div>
  );
};

export default StockFollowButton;
